import { Alert } from "@components/ui/Alert"
import { Button } from "@components/ui/Button"
import { ButtonGroup } from "@components/ui/ButtonGroup"
import { Input } from "@components/ui/Input"
import { alerts } from "@utils/alerts"
import { useRPCMutation } from "eddev/hooks"
import { Link } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { Fragment, ReactNode, useState } from "react"
import { useAuth } from "../useAuth"

export type Props = {
  loginType?: "login" | "signup"
  onNoAccountFound?: (email: string) => void
}

export function useLoginForm({ loginType = "login", onNoAccountFound }: Props) {
  const auth = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  const confirmEmail = useRPCMutation("api.memberships.preLogin")
  const signup = useRPCMutation("api.memberships.signup")

  let [showing, setShowing] = useState<{
    mode: "email" | "password" | "create-password" | "confirmation-email-sent"
    message?: string
    error?: string
  }>({
    mode: "email",
  })

  let fields: ReactNode = null
  let button: ReactNode = null
  let title: ReactNode = null
  let onBack = null as (() => void) | null
  let onSubmit = () => {}
  let [submitting, setSubmitting] = useState(false)

  if (showing.mode === "email") {
    title = "Sign In"
    fields = (
      <Fragment>
        <div className="type-body-xs">Enter your email address to continue</div>
        <Input
          type="email"
          required
          autoComplete="email"
          placeholder="Email Address"
          value={email}
          onValueChange={setEmail}
          disabled={submitting}
        />
        {loginType === "login" && (
          <div>
            <Link href="/membership" className={linkStyle}>
              Become a member
            </Link>
          </div>
        )}
      </Fragment>
    )
    button = (
      <>
        <Button type="submit" loading={submitting} rightIcon="chevronRight">
          Continue
        </Button>
      </>
    )
    onSubmit = async () => {
      await confirmEmail.mutateAsync({ email }).then((result) => {
        if (!result.accountExists) {
          if (onNoAccountFound) {
            onNoAccountFound(email)
          }
          setShowing({ mode: "email", error: "We couldn't find that email in our member database." })
        } else if (result.mustCreatePassword) {
          setShowing({
            mode: "create-password",
            message:
              "It looks like you've got an existing membership, however you'll need to create a new password to continue.",
          })
        } else {
          setShowing({ mode: "password", message: "" })
        }
      })
    }
  } else if (showing.mode === "password") {
    title = `Enter your password`
    onBack = () => setShowing({ mode: "email", message: "" })
    fields = (
      <Fragment>
        {loginType === "login" ? (
          <div className="type-body-xs">You're signing in as {email}</div>
        ) : loginType === "signup" ? (
          <div className="type-body-xs">
            It looks like you've been here before! Please enter your password for {email}
          </div>
        ) : null}
        <Input
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onValueChange={setPassword}
          autoFocus
        />
        <div className="flex flex-wrap gap-2">
          <a
            onClick={async (e) => {
              e.preventDefault()
              const result = await auth.api.auth.resetPasswordForEmail(email, {
                redirectTo: document.location.href,
              })
              if (result.error?.message) {
                alerts.displayAlert({ title: "An error occurred", description: result.error.message })
              } else {
                alerts.displayAlert({
                  title: "Password reset email sent",
                  description: "Check your email for a link to reset your password.",
                })
              }
            }}
            className={linkStyle}
          >
            Reset password
          </a>
          <a
            onClick={async (e) => {
              e.preventDefault()
              setShowing({ mode: "email", message: "" })
            }}
            className={linkStyle}
          >
            Use a different email
          </a>
        </div>
      </Fragment>
    )
    button = (
      <Fragment>
        <Button type="submit" loading={submitting} rightIcon="chevronRight">
          Sign In
        </Button>
      </Fragment>
    )
    onSubmit = async () => {
      setShowing({ mode: "password", message: "" })
      await auth.api.auth.signOut()
      auth.api.auth
        .signInWithPassword({
          email,
          password,
        })
        .then((result) => {
          if (result.error) {
            setShowing({ mode: "password", error: result.error.message })
          }
        })
    }
  } else if (showing.mode === "create-password") {
    title = `Please choose a password`
    fields = (
      <>
        <Input
          type="password"
          autoComplete="new-password"
          placeholder="New Password"
          value={password}
          onValueChange={setPassword}
        />
      </>
    )
    button = (
      <Fragment>
        <Button type="submit" loading={submitting} rightIcon="chevronRight">
          Continue
        </Button>
      </Fragment>
    )
    onSubmit = async () => {
      setSubmitting(true)
      const result = await signup
        .mutateAsync({
          email,
          password,
          origin: window.location.origin,
        })
        .finally(() => {
          setSubmitting(false)
        })
      if (result.error) {
        alerts.displayAlert({
          title: "Oops!",
          description: result.error.message,
        })
      }
      if (result.data.user) {
        setShowing({ mode: "confirmation-email-sent" })
      }
      if (result.data.session) {
        auth.api.auth.setSession(result.data.session)
      }
    }
  } else if (showing.mode === "confirmation-email-sent") {
    title = "Please check your email"
    fields = (
      <>
        <div className="type-body-xs">
          We've sent you an email with a link to confirm your email address. Please check your inbox and click the link
          to continue.
        </div>
      </>
    )
    button = (
      <Fragment>
        <Button type="submit" loading={submitting} rightIcon="chevronRight">
          Okay
        </Button>
      </Fragment>
    )
    onSubmit = async () => {
      setShowing({ mode: "email", message: "" })
    }
  }

  const submit = () => {
    async function submit() {
      setSubmitting(true)
      return onSubmit()
    }
    submit().finally(() => {
      setSubmitting(false)
    })
  }

  return {
    title,
    fields,
    button,
    showing,
    setShowing,
    submit,
  }
}

export function LoginScreen(props: Props) {
  const form = useLoginForm(props)

  return (
    <form
      className="flex flex-col justify-stretch gap-3 w-full"
      onSubmit={(e) => {
        e.preventDefault()
        form.submit()
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div key={form.showing.mode} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="flex w-full flex-col gap-2">
            <div className="stack-x-1 items-center">
              {/* {onBack && (
                <button onClick={onBack} className="aspect-square -ml-2" type="button">
                  <Icon name="chevronLeft" />
                </button>
              )} */}
              <div className="type-label-m mt-1">{form.title}</div>
            </div>
            {form.showing.message && <Alert>{form.showing.message}</Alert>}
            {form.showing.error && <Alert error>{form.showing.error}</Alert>}
            {form.fields}
            <ButtonGroup align="right">{form.button}</ButtonGroup>
          </div>
        </motion.div>
      </AnimatePresence>
    </form>
  )
}

const linkStyle = "underline underline-offset-[0.15em] type-body-xs cursor-pointer"
